import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

interface PinAttempt {
  timestamp: number;
  success: boolean;
}

interface PinToken {
  validUntil: number | null;
  lastActivity: number | null;
}

interface PinContextState {
  pinToken: PinToken;
  pinAttempts: PinAttempt[];
  isPinSessionValid: () => boolean;
  extendPinSession: () => void;
  addPinAttempt: (success: boolean) => void;
  isBlocked: () => boolean;
  getBlockTimeRemaining: () => number;
  clearPinSession: () => void;
  setPinSession: (validUntil: number) => void;
}

const PinContext = createContext<PinContextState | undefined>(undefined);

const MAX_ATTEMPTS = 5;
const ATTEMPT_WINDOW = 60 * 1000; // 1 minuto en milisegundos

export const PinProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Estados para tiempos configurables
  const [blockDuration, setBlockDuration] = useState(60 * 1000); // 1 minuto por defecto en milisegundos

  // Cargar configuración de tiempos al iniciar
  useEffect(() => {
    const loadPinConfig = async () => {
      try {
        const response = await fetch('/api/pin/config', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          // block_duration viene en segundos del backend, convertir a milisegundos
          setBlockDuration((data.block_duration || 60) * 1000);
        }
      } catch (error) {
        console.error('Error cargando configuración de PIN:', error);
        // Usar valores por defecto si falla
      }
    };

    loadPinConfig();
  }, []);

  // Cargar estado desde sessionStorage al iniciar
  const loadPinToken = (): PinToken => {
    try {
      const stored = sessionStorage.getItem('pin_session');
      if (stored) {
        const parsed = JSON.parse(stored);
        // Verificar si la sesión aún es válida
        if (parsed.validUntil && parsed.validUntil > Date.now()) {
          return parsed;
        }
      }
    } catch (error) {
      console.error('Error cargando pin_session desde sessionStorage:', error);
    }
    return { validUntil: null, lastActivity: null };
  };

  const loadPinAttempts = (): PinAttempt[] => {
    try {
      const stored = sessionStorage.getItem('pin_attempts');
      if (stored) {
        const parsed: PinAttempt[] = JSON.parse(stored);
        // Limpiar intentos antiguos (más de 1 minuto)
        const now = Date.now();
        return parsed.filter(attempt => now - attempt.timestamp < ATTEMPT_WINDOW);
      }
    } catch (error) {
      console.error('Error cargando pin_attempts desde sessionStorage:', error);
    }
    return [];
  };

  const [pinToken, setPinToken] = useState<PinToken>(loadPinToken);
  const [pinAttempts, setPinAttempts] = useState<PinAttempt[]>(loadPinAttempts);

  // Guardar estado en sessionStorage cuando cambia
  useEffect(() => {
    if (pinToken.validUntil) {
      sessionStorage.setItem('pin_session', JSON.stringify(pinToken));
    } else {
      sessionStorage.removeItem('pin_session');
    }
  }, [pinToken]);

  useEffect(() => {
    if (pinAttempts.length > 0) {
      sessionStorage.setItem('pin_attempts', JSON.stringify(pinAttempts));
    } else {
      sessionStorage.removeItem('pin_attempts');
    }
  }, [pinAttempts]);

  // Limpiar intentos antiguos periódicamente
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setPinAttempts(prev => prev.filter(attempt => now - attempt.timestamp < ATTEMPT_WINDOW));
    }, 10000); // Cada 10 segundos

    return () => clearInterval(interval);
  }, []);

  const isPinSessionValid = useCallback((): boolean => {
    // Preferir sessionStorage: setPinSession lo escribe en sync antes de onPinVerified,
    // así el resume del control no ve sesión “vieja” por el setState asíncrono.
    try {
      const stored = sessionStorage.getItem('pin_session');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.validUntil && Date.now() < parsed.validUntil) {
          return true;
        }
      }
    } catch {
      /* ignore */
    }
    if (!pinToken.validUntil) return false;
    return Date.now() < pinToken.validUntil;
  }, [pinToken.validUntil]);

  const extendPinSession = useCallback((): void => {
    // NOTA: Esta función ya no se usa - la duración de sesión viene del backend
    // Se mantiene por compatibilidad pero no hace nada
    console.warn('extendPinSession está deprecated - la sesión viene del backend');
  }, []);

  const setPinSession = useCallback((validUntil: number): void => {
    const token: PinToken = {
      validUntil,
      lastActivity: Date.now(),
    };
    try {
      sessionStorage.setItem('pin_session', JSON.stringify(token));
    } catch {
      /* ignore */
    }
    setPinToken(token);
  }, []);

  const addPinAttempt = useCallback((success: boolean): void => {
    const now = Date.now();
    const newAttempt: PinAttempt = { timestamp: now, success };
    
    // Agregar nuevo intento y limpiar intentos antiguos
    setPinAttempts(prev => {
      const filtered = prev.filter(attempt => now - attempt.timestamp < ATTEMPT_WINDOW);
      return [...filtered, newAttempt];
    });

    // No extendemos la sesión aquí - el timestamp viene del backend vía setPinSession
  }, []);

  const isBlocked = useCallback((): boolean => {
    const now = Date.now();
    
    // Filtrar intentos fallidos en la última ventana de tiempo
    const recentFailedAttempts = pinAttempts.filter(
      attempt => !attempt.success && now - attempt.timestamp < ATTEMPT_WINDOW
    );

    // Si hay MAX_ATTEMPTS o más intentos fallidos
    if (recentFailedAttempts.length >= MAX_ATTEMPTS) {
      // Verificar si el bloqueo aún está activo
      const oldestFailedAttempt = recentFailedAttempts[0];
      const blockUntil = oldestFailedAttempt.timestamp + blockDuration;
      
      return now < blockUntil;
    }

    return false;
  }, [pinAttempts, blockDuration]);

  const getBlockTimeRemaining = useCallback((): number => {
    const now = Date.now();
    const recentFailedAttempts = pinAttempts.filter(
      attempt => !attempt.success && now - attempt.timestamp < ATTEMPT_WINDOW
    );

    if (recentFailedAttempts.length >= MAX_ATTEMPTS) {
      const oldestFailedAttempt = recentFailedAttempts[0];
      const blockUntil = oldestFailedAttempt.timestamp + blockDuration;
      return Math.max(0, blockUntil - now);
    }

    return 0;
  }, [pinAttempts, blockDuration]);

  const clearPinSession = useCallback((): void => {
    setPinToken({ validUntil: null, lastActivity: null });
    sessionStorage.removeItem('pin_session');
  }, []);

  const value: PinContextState = {
    pinToken,
    pinAttempts,
    isPinSessionValid,
    extendPinSession,
    addPinAttempt,
    isBlocked,
    getBlockTimeRemaining,
    clearPinSession,
    setPinSession
  };

  return <PinContext.Provider value={value}>{children}</PinContext.Provider>;
};

export const usePinContext = (): PinContextState => {
  const context = useContext(PinContext);
  if (!context) {
    throw new Error('usePinContext debe usarse dentro de un PinProvider');
  }
  return context;
};

